import { io } from 'socket.io-client';

import type { MessagePayload } from '../types/ChatWindow';
import type { IOBundle, IOEvent } from '../types/io';
import { getUserId } from './localStorageService';
import { sendLogMessage } from './postMessageService';

type IO = ReturnType<typeof io>;

let IOInstance: IO | null = null;
let initialUri: string;
let initialBotId: string;
let connected = false;

const ioConfig = {
  path: '/sockets',
  transports: ['websocket'],
};

const init = async (
  uri: string,
  botId: string,
  configId: string,
  onRegisterEvent: () => void,
  onConnectionStatusChangeCallback?: (boolean) => void,
) => {
  initialUri = uri;
  initialBotId = botId;
  if (!IOInstance) {
    console.log(`Initializing IO service for ${botId}`);
    IOInstance = io(`${uri}/web-client`, {
      query: {
        id: getUserId(configId),
        'conversed-bot': botId,
      },
      ...ioConfig,
    });

    // Wait for the socket to connect and get the id
    IOInstance.on('connect', async () => {
      const id = await getUserId(configId);
      if (id) {
        send({
          event: 'id',
          data: { id, type: 'id', newId: { id: getSocketID() } },
        });
      }

      // * Reconnect only if socket has been connected before
      if (connected) {
        onConnectionStatusChangeCallback?.(true);
      }

      sendLogMessage(
        `Websocket ${IOInstance?.recovered ? 'reconnected' : 'connected'}`,
        'info',
      );

      connected = true;
      onRegisterEvent();
    });

    IOInstance.on('disconnect', async () => {
      sendLogMessage('Websocket disconnected', 'error');
      connected = false;
      onConnectionStatusChangeCallback?.(false);
    });
  } else {
    // If the websocket connection is already established, just register the events again ( example: after closing and re-opening the chat window )
    onRegisterEvent();
  }
};

const registerEvent = ({ event, callback }: IOEvent) => {
  IOInstance?.on(event, (payload: MessagePayload) => {
    const direction = payload?.type === 'system' ? 'system' : 'out';
    callback(direction, payload);
  });
};

const send = ({ event, data }: IOBundle) => {
  IOInstance?.emit(event, data);
};

const disconnect = () => {
  IOInstance?.disconnect();
};

const reInitialize = (configId: string, onRegisterEvent: () => void) => {
  disconnect();
  IOInstance = null;
  init(initialUri, initialBotId, configId, onRegisterEvent);
};

const removeListeners = () => {
  IOInstance?.removeAllListeners();
};

// Return whether the socket is currently connected
const isConnected = () => {
  return connected;
};

export function getSocketID() {
  return IOInstance?.id;
}

export default {
  init,
  reInitialize,
  registerEvent,
  send,
  disconnect,
  removeListeners,
  isConnected,
};
